import { User, Terminal } from 'lucide-react';
import React from "react";

interface ParsedComment {
  id: string;
  mainComment: string;
  author: string;
  isSystem: boolean;
  showAuthor: boolean;
}

const parseCommentsString = (comments?: string | null): ParsedComment[] => {
  if (!comments) {
    return [];
  }

  return comments
    .split('\n')
    .filter(row => row.length > 1)
    .map((row, index) => {
      const delimiter = ' by ';
      const lastDelimiterIndex = row.lastIndexOf(delimiter);

      let mainComment: string;
      let author: string;
      let showAuthor: boolean;

      if (lastDelimiterIndex === -1) {
        // No delimiter, the whole row is the comment.
        mainComment = row;
        author = row;
        showAuthor = false;
      } else {
        // Delimiter found.
        mainComment = row.substring(0, lastDelimiterIndex);
        author = row.substring(lastDelimiterIndex + delimiter.length);
        showAuthor = true;

        if (!author) {
          return null;
        }
      }

      return {
        id: `${row}-${index}`,
        mainComment,
        author,
        isSystem: author.trim() === "echo@randmar.io" || author.trim() === "Echo",
        showAuthor,
      };
    }).filter((c): c is ParsedComment => c !== null);
};

interface CommentsViewProps {
  comments?: string;
}

const CommentsView: React.FC<CommentsViewProps> = ({ comments }) => {
  const parsedComments = React.useMemo(() => parseCommentsString(comments), [comments]);

  return (
    <div className="w-full space-y-3">
      {parsedComments.map((comment) => (
        <article key={comment.id} className="flex items-start space-x-4 p-4 bg-white rounded-xl border w-full">
          <div className="flex-shrink-0">
            <div className="bg-slate-100 rounded-full p-2">
              {comment.isSystem ?
                <Terminal className="h-5 w-5 text-slate-600" /> :
                <User className="h-5 w-5 text-slate-600" />
              }
            </div>
          </div>
          <div className="flex-grow pt-1">
            <p className="text-sm text-slate-800">{comment.mainComment}</p>
            {comment.showAuthor && <p className="text-xs text-slate-500 mt-1 italic">by {comment.author}</p>}
          </div>
        </article>
      ))}
    </div>
  );
};

export default CommentsView;
